import { Box, Fade, makeStyles } from '@material-ui/core';
import useCallAgentPermissionFlags from '@src/hooks/call/useCallAgentPermissionFlags';
import useCallStatusFlags from '@src/hooks/call/useCallStatusFlags';
import { MessagingProvider } from '@src/model/frontendmodel';
import { selectActiveCallId } from '@src/selectors/calls';
import { showWarningTraysOnHold } from '@src/selectors/chatAutoAssignment';
import { activeChatSideSelector } from '@src/selectors/chats';
import { ChatSideType, ChatTray } from '@src/types/chats';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { VariableSizeList as List } from 'react-window';
import ChatListItem from '../ChatListItem';
import IncomingCall from './IncomingCall';
import useChatCallSync from './useChatCallSync';

export type ChatListItemsProps = {
  items: ChatTray[];
  height: number;
  onScrollBottom: () => void;
};

const itemSize = 80;
const itemSizeAssignment = itemSize + 10;

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.background.paper
        : theme.palette.grey[50],
  },
}));

const ChatListItems: React.FC<ChatListItemsProps> = ({
  items,
  height,
  onScrollBottom,
}) => {
  const listRef = useRef<List>();
  const lastTopChat = useRef<ChatTray | null>(null);

  const [scrollPosition, setScrollPosition] = useState(0);

  const itemKey = useCallback((index: number) => items[index].id, [items]);

  const qtyItemsWithAssignment = items.reduce((acc, item) => {
    if (item.assignedBy?.agent?.id) return acc + 1;
    return acc;
  }, 0);

  useEffect(() => {
    listRef?.current?.scrollTo(scrollPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qtyItemsWithAssignment]);

  useEffect(() => {
    if (items.length === 0) return;
    const currentTopChat = items[0];

    const isAssignedCurrent = Boolean(currentTopChat.assignedBy?.agent?.id);
    const isAssignedLast = Boolean(lastTopChat.current?.assignedBy?.agent?.id);

    // Note: this condition checks if the list order has changed, and also if the previous top item has a different height
    //       than the current top item. We determine this by checking if one of them has an assignment and the other does not.
    //       We need to reset the list cache in that case to avoid visual chat cutoffs.

    if (isAssignedCurrent !== isAssignedLast) {
      lastTopChat.current = currentTopChat;
      if (listRef?.current) {
        listRef.current.resetAfterIndex(0);
        listRef.current.resetAfterIndex(1);
      }
    }
  }, [items]);

  return (
    <List
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      ref={listRef}
      key={qtyItemsWithAssignment}
      width="100%"
      height={height}
      itemSize={index =>
        items[index]?.assignedBy?.agent ? itemSizeAssignment : itemSize
      }
      estimatedItemSize={itemSizeAssignment}
      itemData={items}
      className="List"
      itemCount={items.length ?? 0}
      style={{ overflowX: 'hidden' }}
      onScroll={event => {
        if (event.scrollOffset) setScrollPosition(event?.scrollOffset);

        const offset = itemSize;
        const scrollAt = height + event.scrollOffset + offset;
        const heightListItems = itemSize * items.length;
        if (scrollAt >= heightListItems) onScrollBottom();
      }}
      itemKey={itemKey}
    >
      {ChatListItemRow}
    </List>
  );
};
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const ChatListItemRow = ({ index, style, data }) => {
  const { root } = useStyles();

  const chat = data[index] as ChatTray;
  const activeChatSide = useSelector(activeChatSideSelector);
  const isBlocked = useSelector(showWarningTraysOnHold);
  const activeCallId = useSelector(selectActiveCallId);
  const call = useChatCallSync(chat);
  const callStatusFlags = useCallStatusFlags(call?.status);
  const { isVisible } = useCallAgentPermissionFlags();

  const isActiveOnHoldSide = activeChatSide === ChatSideType.ON_HOLD;
  const isActiveBotSide = activeChatSide === ChatSideType.BOT;
  const blocked =
    isBlocked &&
    !isActiveOnHoldSide &&
    !isActiveBotSide &&
    !chat.chatXferRequest;

  const hasAssignedAgent = useMemo(
    () => Boolean(chat?.assignedBy?.agent?.id),
    [chat?.assignedBy?.agent]
  );

  const isShowCallRequest = useMemo(
    () =>
      call?.id &&
      call?.id !== activeCallId &&
      (callStatusFlags.isCallMissed || callStatusFlags.isCallWaitingAgent) &&
      chat.provider === MessagingProvider.WHATSAPPB2CHAT,
    [
      activeCallId,
      call?.id,
      callStatusFlags.isCallMissed,
      callStatusFlags.isCallWaitingAgent,
      chat.provider,
    ]
  );

  return (
    <div
      className={`${blocked ? 'ChatRequestsBox-blocked' : ''} ${root}`}
      id={chat?.id}
      key={chat?.id}
      style={style}
    >
      <div
        style={{
          position: 'relative',
          height: hasAssignedAgent ? itemSizeAssignment : itemSize,
        }}
      >
        <Box style={{ display: 'flex', height: 'inherit' }}>
          {isVisible && !!isShowCallRequest && (
            <Fade in>
              <IncomingCall call={call!} chatSide={activeChatSide} />
            </Fade>
          )}
          <ChatListItem chat={chat} showDivider={!hasAssignedAgent} />
        </Box>
      </div>
    </div>
  );
};

export default ChatListItems;
